import { Broadcast, ToBlob } from "@pdfking/tfunc"

import type { NewDocument } from "../types/NewDocument"
import { GetLastError } from "./GetLastError"

/**
 * Creates an AsyncIterable that yields chunks of the PDF document
 *
 * @param document The PDF document to save
 * @returns AsyncIterable of Uint8Array chunks
 */
export const SaveDocument = ({
  pdfium,
  doc_pointer,
}: NewDocument): AsyncGenerator<Blob> => {
  // Create a broadcast instance to emit chunks
  const { stop, push, subscribe } = Broadcast<Blob>()

  // Create a subscriber immediately (this is what we'll return)
  const subscriber = subscribe()

  // Create a write callback function that broadcasts chunks
  const write_callback = (_, data, size) => {
    // Copy data from PDFium's memory
    const bytes = new Uint8Array(
      pdfium.pdfium.HEAPU8.buffer,
      pdfium.pdfium.HEAPU8.byteOffset + data,
      size
    )

    // Make a copy to ensure we own the memory
    // Broadcast the chunk to all subscribers
    //
    push(ToBlob(bytes.slice()))

    return size
  }

  const runner = async () => {
    // Start the PDF saving process
    // Add the callback function to PDFium's function table
    const write_callback_pointer = pdfium.pdfium.addFunction(
      write_callback,
      "iiii"
    )

    // Create FPDF_FILEWRITE structure
    // Struct has 2 fields: version (int) and WriteBlock function pointer
    const file_write_struct = pdfium.pdfium.wasmExports.malloc(8) // 4 bytes for version + 4 bytes for function pointer

    // Set version = 1
    pdfium.pdfium.setValue(file_write_struct, 1, "i32")

    // Set WriteBlock function pointer
    pdfium.pdfium.setValue(file_write_struct + 4, write_callback_pointer, "i32")

    // Save the PDF using the callback
    // Using FPDF_NO_INCREMENTAL (2) instead of 0 or FPDF_INCREMENTAL (1)
    // to avoid timestamp-related issues
    const result = pdfium.FPDF_SaveAsCopy(
      doc_pointer,
      file_write_struct,
      2 // FPDF_NO_INCREMENTAL
    )

    pdfium.pdfium.wasmExports.free(file_write_struct)
    pdfium.pdfium.removeFunction(write_callback_pointer)

    if (result) {
      stop(undefined)
    } else {
      stop(new Error(GetLastError(pdfium)))
    }
  }

  void runner()

  // Return the subscriber which is an AsyncGenerator
  return subscriber
}
